import type { VtkModules } from './loadVtkModules';

export type CameraState = {
  pos: number[];
  fp: number[];
  up: number[];
  cr: number[];
};

export type VtkScene = {
  renderWindow: unknown;
  renderer: unknown;
  openGLRenderWindow: unknown;
  interactor: unknown;

  mapper: unknown;
  actor: unknown;
  polydata: unknown;

  streamlineMapper: unknown;
  streamlineActor: unknown;
  streamlinePolydata: unknown;

  scalarBarActor: unknown | null;

  destroy: () => void;
  resize: () => void;
};

export function createVtkScene(
  vtk: VtkModules,
  container: HTMLDivElement,
): VtkScene {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const renderWindow = (vtk.vtkRenderWindow as any).newInstance();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const renderer = (vtk.vtkRenderer as any).newInstance({
    background: [0.1, 0.1, 0.1],
  });
  renderWindow.addRenderer(renderer);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const openGLRenderWindow = (vtk.vtkOpenGLRenderWindow as any).newInstance();
  openGLRenderWindow.setContainer(container);
  renderWindow.addView(openGLRenderWindow);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const interactor = (vtk.vtkInteractor as any).newInstance();
  interactor.setView(openGLRenderWindow);
  interactor.initialize();
  interactor.bindEvents(container);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  interactor.setInteractorStyle((vtk.vtkInteractorStyle as any).newInstance());

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const mapper = (vtk.vtkMapper as any).newInstance();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const actor = (vtk.vtkActor as any).newInstance();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const polydata = (vtk.vtkPolyData as any).newInstance();

  mapper.setInputData(polydata);
  actor.setMapper(mapper);
  renderer.addActor(actor);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const streamlineMapper = (vtk.vtkMapper as any).newInstance();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const streamlineActor = (vtk.vtkActor as any).newInstance();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const streamlinePolydata = (vtk.vtkPolyData as any).newInstance();

  streamlineMapper.setInputData(streamlinePolydata);
  streamlineActor.setMapper(streamlineMapper);
  streamlineActor.getProperty().setLineWidth(2);
  streamlineActor.setVisibility(false);
  renderer.addActor(streamlineActor);

  const resize = () => {
    const rect = container.getBoundingClientRect();
    openGLRenderWindow.setSize(
      Math.max(1, Math.floor(rect.width)),
      Math.max(1, Math.floor(rect.height)),
    );
    renderWindow.render();
  };

  resize();

  return {
    renderWindow,
    renderer,
    openGLRenderWindow,
    interactor,

    mapper,
    actor,
    polydata,

    streamlineMapper,
    streamlineActor,
    streamlinePolydata,

    scalarBarActor: null,

    resize,
    destroy: () => {
      try {
        interactor.unbindEvents();
      } catch (e) {
        // Ignore errors during unbinding
      }

      try {
        [
          actor,
          mapper,
          polydata,
          streamlineMapper,
          streamlineActor,
          streamlinePolydata,
          openGLRenderWindow,
          interactor,
          renderer,
          renderWindow,
        ].forEach((obj) => obj?.delete?.());
      } catch (e) {
        // Ignore errors during cleanup
      }
    },
  };
}
